import { formatTokens, parseTimestampMs } from '../../components/formatters.ts';

/**
 * Shared display helpers for the usage-analytics group (`/usage` +
 * `/requests`, epic #897 PR 6). Both pages previously carried private
 * copies of these (compact counts, percent/ratio rendering, range math)
 * — this module is their ONE home so the two pages cannot drift.
 *
 * Everything here is a pure formatter or pure math; anything needing
 * translation takes the display string from the caller.
 */

const COMPACT_FORMATTER = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

const PERCENT_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'percent',
  maximumFractionDigits: 1,
});

// ─── Local-day range math (date-range filter, /usage + /requests) ─────────
//
// The calendar filter works in LOCAL `YYYY-MM-DD` day strings end to
// end; only `dayRangeToIsoWindow` converts to instants, at the moment
// the query is built. Lexicographic comparison on these strings is
// chronological, which the helpers below rely on.

export interface DayRange {
  readonly from: string;
  readonly to: string;
}

export interface DayRangePresetShape {
  readonly id: string;
  readonly range: DayRange;
}

export function localDayString(date: Date): string {
  return [
    String(date.getFullYear()).padStart(4, '0'),
    pad2(date.getMonth() + 1),
    pad2(date.getDate()),
  ].join('-');
}

export function todayLocalDay(): string {
  return localDayString(new Date());
}

export function shiftLocalDay(day: string, deltaDays: number): string {
  const date = dayToLocalDate(day);
  date.setDate(date.getDate() + deltaDays);
  return localDayString(date);
}

/** Inclusive rolling window: `days` calendar days ending on `today`. */
export function rollingDayRange(today: string, days: number): DayRange {
  return { from: shiftLocalDay(today, -(days - 1)), to: today };
}

export function monthToDateRange(today: string): DayRange {
  return { from: `${today.slice(0, 8)}01`, to: today };
}

/** ISO instant window covering the inclusive local-day range. */
export function dayRangeToIsoWindow(range: DayRange): {
  readonly from: string;
  readonly to: string;
} {
  const from = dayToLocalDate(range.from);
  const to = dayToLocalDate(range.to);
  to.setHours(23, 59, 59, 999);
  return { from: from.toISOString(), to: to.toISOString() };
}

/** Validate a URL day param: `YYYY-MM-DD` shape AND a real calendar date. */
export function parseDayParam(value: string | null): string | null {
  if (value === null || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const date = dayToLocalDate(value);
  return localDayString(date) === value ? value : null;
}

/**
 * Resolve URL search params to a range selection. A `rangeParam`
 * matching a preset id (directly or through `aliases`, e.g. the legacy
 * `24h`) wins; `custom` requires a valid, non-inverted, non-future
 * window within `maxSpanDays`. Anything else falls back to
 * `defaultPresetId` — the backend 400 guard stays as the last line.
 */
export function resolveRangeSelection(options: {
  readonly rangeParam: string | null;
  readonly fromParam: string | null;
  readonly toParam: string | null;
  readonly presets: readonly DayRangePresetShape[];
  readonly defaultPresetId: string;
  readonly maxSpanDays: number;
  readonly today: string;
  readonly aliases?: Readonly<Record<string, string>>;
}): { readonly presetId: string | null; readonly range: DayRange } {
  const resolvedId =
    options.rangeParam !== null
      ? (options.aliases?.[options.rangeParam] ?? options.rangeParam)
      : null;
  if (resolvedId !== null && resolvedId !== 'custom') {
    const preset = options.presets.find((candidate) => candidate.id === resolvedId);
    if (preset !== undefined) return { presetId: preset.id, range: preset.range };
  }
  if (resolvedId === 'custom') {
    const from = parseDayParam(options.fromParam);
    const to = parseDayParam(options.toParam);
    if (
      from !== null &&
      to !== null &&
      from <= to &&
      to <= options.today &&
      daySpanInclusive(from, to) <= options.maxSpanDays
    ) {
      return { presetId: null, range: { from, to } };
    }
  }
  const fallback = options.presets.find((candidate) => candidate.id === options.defaultPresetId);
  if (fallback !== undefined) return { presetId: fallback.id, range: fallback.range };
  // Unreachable when callers pass a defaultPresetId from their own
  // preset list; a same-day range keeps the type honest regardless.
  return { presetId: null, range: { from: options.today, to: options.today } };
}

/** Inclusive endpoint count: `2026-06-01..2026-06-01` is 1 day. */
export function daySpanInclusive(from: string, to: string): number {
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  return (
    Math.round((dayToLocalDate(to).getTime() - dayToLocalDate(from).getTime()) / MS_PER_DAY) + 1
  );
}

function dayToLocalDate(day: string): Date {
  return new Date(
    Number.parseInt(day.slice(0, 4), 10),
    Number.parseInt(day.slice(5, 7), 10) - 1,
    Number.parseInt(day.slice(8, 10), 10),
  );
}

/**
 * Token-count display: exact grouping below 10k, compact (`324.6M`)
 * above — large counts stay scannable in KPI cards and table cells.
 */
export function formatCompactCount(count: number): string {
  if (!Number.isFinite(count) || count < 0) return '--';
  if (count < 10000) return formatTokens(count);
  return COMPACT_FORMATTER.format(Math.floor(count));
}

export function formatPercent(value: number): string {
  if (!Number.isFinite(value)) return '--';
  return PERCENT_FORMATTER.format(value);
}

export function formatNullablePercent(value: number | null): string {
  return value === null ? '--' : formatPercent(value);
}

export function formatRatio(value: number | null): string {
  return value === null || !Number.isFinite(value) ? '--' : `${value.toFixed(1)}x`;
}

export function nullableRatio(numerator: number, denominator: number): number | null {
  return denominator > 0 ? numerator / denominator : null;
}

export function formatLatencyMs(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '--';
  return `${formatTokens(value)} ms`;
}

/** Local-time `YYYY-MM-DD` for window boundaries. */
export function formatIsoDate(iso: string): string {
  const ms = parseTimestampMs(iso);
  if (!Number.isFinite(ms)) return iso.length > 0 ? iso : '--';
  const d = new Date(ms);
  return [d.getFullYear(), pad2(d.getMonth() + 1), pad2(d.getDate())].join('-');
}

/** Local-time `HH:mm` for "refreshed at" style stamps. */
export function formatShortTime(iso: string): string {
  const ms = parseTimestampMs(iso);
  if (!Number.isFinite(ms)) return '--';
  const d = new Date(ms);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

export function testIdSlug(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

/**
 * Option label for API-key filter selects: prefer the human name, fall
 * back to the key prefix (always present), and mark revoked keys with
 * the translated suffix passed by the caller.
 */
export function apiKeyOptionLabel(
  key: { readonly name: string | null; readonly prefix: string; readonly revokedAt: string | null },
  revokedSuffix: string,
): string {
  const base = key.name !== null && key.name.length > 0 ? key.name : key.prefix;
  return key.revokedAt !== null ? `${base}${revokedSuffix}` : base;
}
